import { ButtonField, Icon } from '@wk/components-v3-react16';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useState } from 'react';
import clsx from 'clsx';
import { Module } from '../../../DraggableModules/DraggableModules';
import {
  failbackStepGroupAtom,
  nextStepGroupAtom,
} from '../../../workflowFieldAtoms';
import { useSettingsCanvas } from '../../../WorkflowTabs/useSettingsCanvas';
import { useWorkflowTabs } from '../../../WorkflowTabs/useWorkflowTabs';
import { deleteById } from '../../../workflowTreeUtils';
import DropZone from '../DropZone/DropZone';
import { generateNumericID } from '../../../utils';
import { useSidePanelStep } from './SidePanelForStep/useSidePanelStep';
import styles from './TreeLayout.module.scss';
import {
  disableBackgroundColorAtom,
  hideStepInfoAtom,
  selectedStepIdAtom,
  workflowAtom,
} from '../../../atoms';
import { ServerStepGroup } from '../../../ServerWorkflowType';
import { useHighlightRemovable } from './useHighlightRemovable';
import DeleteModal from './DeleteModal/DeleteModal';

const StepGroup = ({
  steps,
  workflowStepGroupId,
  nextStepGroup,
  parentWorkflowStepGroupId,
  isFailbackStepGroup = false,
  isNestedStepGroup = false,
  isGlobalFailback = false,
  level = 1,
  group = 1,
  groupPath = '1',
  isCompact = false,
}: ServerStepGroup & {
  group?: number;
  groupPath?: string;
  isCompact?: boolean;
  isFailbackStepGroup?: boolean;
  isGlobalFailback?: boolean;
  isNestedStepGroup?: boolean;
  level?: number;
}) => {
  const [mainGroup, setMainGroup] = useAtom(nextStepGroupAtom);

  const [failbackMainGroup, setFailbackMainGroup] = useAtom(
    failbackStepGroupAtom
  );
  const setStep = useSetAtom(selectedStepIdAtom);
  const disableBackgroundColor = useAtomValue(disableBackgroundColorAtom);
  const hideStepInfo = useAtomValue(hideStepInfoAtom);
  const { openSidePanelStep } = useSidePanelStep();
  const {
    handleMouseEnter,
    handleMouseLeave,
    isHighlightedAsRemovable,
    setRemovableIds,
  } = useHighlightRemovable();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [modalPosition, setModalPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const bgStyles = {
    [styles.nestedStepGroup]: isNestedStepGroup && !disableBackgroundColor,
    [styles.failbackStepGroup]: isFailbackStepGroup && !disableBackgroundColor,
    [styles.defaultStepGroup]:
      !isNestedStepGroup && !isFailbackStepGroup && !disableBackgroundColor,
  };

  const handleDeleteConfirm = () => {
    const serverStepGroup = isGlobalFailback ? failbackMainGroup : mainGroup;
    const newStepGroup = deleteById(serverStepGroup, deleteTargetId);
    const setGroupFunction = isGlobalFailback
      ? setFailbackMainGroup
      : setMainGroup;
    setGroupFunction(newStepGroup);
    setIsDeleteModalOpen(false);
    setDeleteTargetId(null);
    setModalPosition(null);
    setRemovableIds([]);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDeleteTargetId(null);
    setModalPosition(null);
    setRemovableIds([]);
  };

  const handleDeleteClick = (
    event: React.MouseEvent,
    stepId: number | null
  ) => {
    event.stopPropagation();

    const targetElement = event.currentTarget as HTMLElement;
    const rect = targetElement.getBoundingClientRect();

    // Position modal to the right of the clicked element, vertically centered
    // getBoundingClientRect returns viewport coordinates that already account for zoom and pan
    const modalX = rect.right + -50;
    const modalY = rect.top + rect.height / 2;

    setDeleteTargetId(stepId);
    setModalPosition({ x: modalX, y: modalY });
    setIsDeleteModalOpen(true);
  };
  return (
    <div
      className={clsx(styles.groupOfGroups, styles.padding12, {
        [styles.containerStyle]: !isCompact,
        ...bgStyles,
      })}
    >
      <div
        className={clsx(styles.steps, styles.padding12, {
          [styles.containerStyle]: !isCompact,
          ...bgStyles,
        })}
      >
        {steps?.map((serverStep, indexOrder) => {
          const {
            nestedStepGroup,
            module,
            workflowStepGroupId,
            workflowStepId,
            failbackStepGroup,
            stepCondition,
          } = serverStep;
          // Only check workflowStepId for step highlighting, not workflowStepGroupId
          const isHighlighted = isHighlightedAsRemovable(workflowStepId);
          const serverStepGroup = isGlobalFailback
            ? failbackMainGroup
            : mainGroup;
          const id = `module-Group-${groupPath}-Level-${level}-Order-${
            indexOrder + 1
          }`;
          return (
            <div
              key={workflowStepId}
              className={clsx(styles.nodeWrapper, styles.padding12, {
                [styles.containerStyle]: !isCompact,
                ...bgStyles,
              })}
            >
              <div
                className={clsx('disable-panning', styles.node, {
                  [styles.highlightRemovable]: isHighlighted,
                })}
                onClick={() => {
                  setStep(workflowStepId);
                  openSidePanelStep();
                }}
              >
                {!hideStepInfo && (
                  <div className={clsx(styles.nodeType)}>
                    <div>{`Path: ${groupPath}`}</div>
                    <div>{`Depth: ${level}`}</div>
                    <div>{`Step: ${indexOrder + 1}`}</div>
                  </div>
                )}
                {!!stepCondition && (
                  <div className={clsx(styles.ifDiv)}>
                    <span>If</span>
                  </div>
                )}
                <div
                  className={clsx(styles.nodeIcons)}
                  onMouseEnter={() =>
                    handleMouseEnter({
                      ServerStepGroup: serverStepGroup || null,
                      targetId: workflowStepId,
                    })
                  }
                  onMouseLeave={() => {
                    if (!isDeleteModalOpen) {
                      handleMouseLeave();
                    }
                  }}
                  onClick={(e) => handleDeleteClick(e, workflowStepId)}
                >
                  <ButtonField
                    type="primary"
                    flowClass={clsx(styles.remove, {
                      [styles.rotateIcon]: isHighlighted,
                    })}
                  >
                    <button type="button">
                      <Icon name="wk-icon-trash" />
                    </button>
                  </ButtonField>
                </div>
                <Module module={module} id={id} />
              </div>
              {(!nestedStepGroup || !failbackStepGroup) && (
                <div className={clsx(styles.wrapperDropZone)}>
                  {!nestedStepGroup && (
                    <DropZone
                      workflowStepGroupId={workflowStepGroupId}
                      workflowStepId={workflowStepId}
                      parentWorkflowStepGroupId={workflowStepId}
                      isGlobalFailback={isGlobalFailback}
                      isNested
                    />
                  )}
                  {!failbackStepGroup && (
                    <DropZone
                      workflowStepGroupId={workflowStepGroupId}
                      workflowStepId={workflowStepId}
                      parentWorkflowStepGroupId={workflowStepId}
                      isGlobalFailback={isGlobalFailback}
                      isFailback
                    />
                  )}
                </div>
              )}
              {(nestedStepGroup || failbackStepGroup) && (
                <div
                  className={clsx(styles.branchWrapper, styles.padding12, {
                    [styles.containerStyle]: !isCompact,
                  })}
                >
                  {nestedStepGroup && (
                    <>
                      <StepGroup
                        {...nestedStepGroup}
                        isNestedStepGroup={true}
                        isGlobalFailback={isGlobalFailback}
                        level={level + 1}
                        isCompact={isCompact}
                        group={group}
                        groupPath={`${groupPath}.N`}
                      />
                      {nestedStepGroup.nextStepGroup && (
                        <NextStepGroup
                          {...nestedStepGroup}
                          group={group}
                          groupPath={`${groupPath}.N`}
                          isCompact={isCompact}
                          isGlobalFailback={isGlobalFailback}
                        />
                      )}
                    </>
                  )}
                  {failbackStepGroup && (
                    <>
                      <StepGroup
                        {...failbackStepGroup}
                        isFailbackStepGroup={true}
                        isGlobalFailback={isGlobalFailback}
                        level={level + 1}
                        isCompact={isCompact}
                        group={group}
                        groupPath={`${groupPath}.F`}
                      />
                      {failbackStepGroup.nextStepGroup && (
                        <NextStepGroup
                          {...failbackStepGroup}
                          group={group}
                          groupPath={`${groupPath}.F`}
                          isCompact={isCompact}
                          isGlobalFailback={isGlobalFailback}
                        />
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <div
          className={clsx(styles.padding12, {
            [styles.containerStyle]: !isCompact,
            [styles.parallelStepGroup]: !disableBackgroundColor,
            ...bgStyles,
          })}
        >
          <DropZone
            isGlobalFailback={isGlobalFailback}
            workflowStepGroupId={workflowStepGroupId || generateNumericID()}
            parentWorkflowStepGroupId={parentWorkflowStepGroupId}
            isParallelStep
          />
        </div>
      </div>
      {/* Only render drop zone for nested/failback branches, not for nextStepGroup chain */}
      {!nextStepGroup && (isNestedStepGroup || isFailbackStepGroup) && (
        <DropZone
          isNextStepGroup
          workflowStepGroupId={workflowStepGroupId}
          parentWorkflowStepGroupId={parentWorkflowStepGroupId}
          isGlobalFailback={isGlobalFailback}
        />
      )}
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        serverStepGroup={isGlobalFailback ? failbackMainGroup : mainGroup}
        targetId={deleteTargetId}
        position={modalPosition}
      />
    </div>
  );
};

const NextStepGroup = (
  properties: ServerStepGroup & {
    group: number;
    groupPath?: string;
    isCompact?: boolean;
    isGlobalFailback?: boolean;
  }
) => {
  const {
    nextStepGroup,
    group,
    groupPath = '1',
    isGlobalFailback,
    isCompact = false,
  } = properties;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '6px',
      }}
    >
      {/* Always show "Create previous step group" at the beginning */}
      {nextStepGroup && (
        <DropZone
          isPreviousStepGroup
          workflowStepGroupId={nextStepGroup.workflowStepGroupId}
          parentWorkflowStepGroupId={nextStepGroup.parentWorkflowStepGroupId}
          isGlobalFailback={isGlobalFailback}
        />
      )}
      {nextStepGroup && (
        <>
          <StepGroup
            {...nextStepGroup}
            group={group + 1}
            groupPath={group === 0 ? groupPath : `${groupPath}.${group + 1}`}
            level={1}
            isCompact={isCompact}
            isGlobalFailback={isGlobalFailback}
          />
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              justifyContent: 'space-between',
            }}
          >
            {nextStepGroup.nextStepGroup ? (
              <NextStepGroup
                {...nextStepGroup}
                group={group + 1}
                groupPath={
                  group === 0 ? groupPath : `${groupPath}.${group + 1}`
                }
                isCompact={isCompact}
                isGlobalFailback={isGlobalFailback}
              />
            ) : (
              <DropZone
                isNextStepGroup
                workflowStepGroupId={nextStepGroup.workflowStepGroupId}
                parentWorkflowStepGroupId={
                  nextStepGroup.parentWorkflowStepGroupId
                }
                isGlobalFailback={isGlobalFailback}
              />
            )}
          </div>
        </>
      )}
      {!nextStepGroup && (
        <DropZone
          isNextStepGroup
          isGlobalFailback={isGlobalFailback}
          workflowStepGroupId={undefined}
        />
      )}
    </div>
  );
};

const GlobalFailbackStepGroup = (
  properties: ServerStepGroup & {
    group: number;
    isCompact?: boolean;
    isGlobalFailback?: boolean;
  }
) => {
  const {
    nextStepGroup,
    steps,
    workflowStepGroupId,
    parentWorkflowStepGroupId,
    group,
    isGlobalFailback,
    isCompact = false,
  } = properties;

  return (
    <>
      {/* Always show "Create previous step group" at the beginning */}
      <DropZone
        isPreviousStepGroup
        workflowStepGroupId={workflowStepGroupId}
        parentWorkflowStepGroupId={parentWorkflowStepGroupId}
        isGlobalFailback={isGlobalFailback}
      />
      {/* Render the initial failback step group - don't pass nextStepGroup since we handle it below */}
      <StepGroup
        steps={steps}
        workflowStepGroupId={workflowStepGroupId}
        parentWorkflowStepGroupId={parentWorkflowStepGroupId}
        isGlobalFailback={isGlobalFailback}
        group={group + 1}
        level={1}
        isCompact={isCompact}
      />
      {/* Then render the chain of nextStepGroups at the global failback level */}
      {nextStepGroup ? (
        <GlobalFailbackStepGroup
          {...nextStepGroup}
          group={group + 1}
          isGlobalFailback={isGlobalFailback}
          isCompact={isCompact}
        />
      ) : (
        <DropZone
          isNextStepGroup
          workflowStepGroupId={workflowStepGroupId}
          parentWorkflowStepGroupId={parentWorkflowStepGroupId}
          isGlobalFailback={isGlobalFailback}
        />
      )}
    </>
  );
};

const TreeLayout = () => {
  const workflow = useAtomValue(workflowAtom);
  const { handleMouseUp, handleMouseDown, isMousePressed } =
    useSettingsCanvas();
  const { isCompact } = useSettingsCanvas();
  const { selectedTab } = useWorkflowTabs();
  if (selectedTab === 1) {
    return (
      <div className={styles.treeWrapper}>
        {workflow.failbackStepGroup ? (
          <GlobalFailbackStepGroup
            {...workflow.failbackStepGroup}
            isGlobalFailback={true}
            group={0}
            isCompact={isCompact}
          />
        ) : (
          <DropZone
            isNextStepGroup
            isGlobalFailback={true}
            workflowStepGroupId={undefined}
          />
        )}
      </div>
    );
  }
  return (
    <div
      className={styles.treeWrapper}
      onMouseUp={handleMouseUp}
      onMouseDown={handleMouseDown}
      style={{ cursor: isMousePressed ? 'grabbing' : 'grab' }}
    >
      <NextStepGroup
        {...workflow}
        group={0}
        groupPath="1"
        isCompact={isCompact}
      />
    </div>
  );
};

export default TreeLayout;
